import styled, { css } from 'styled-components/macro'
import { Spacing, spacingMixin } from 'styles/spacing'
import { Color } from 'styles/types'
import { resolveResponsiveProp } from 'styles/mixins'
import { Direction } from 'components/shared/Box'
import { useResolutions } from 'store/resolutions'

interface SeparatedListProps extends Spacing {
    gap: number | [number, number]
    borderColor?: Color
    borderWidth?: number | [number, number]
    direction: Direction | [Direction, Direction]
    isFullWidth?: boolean
    hasLastItemBorder?: boolean
    isDashed?: boolean
    turnOffChildTopBorder?: number[]
}

const SeparatedList = styled.div<SeparatedListProps>`
    display: flex;

    ${({ isFullWidth }) =>
        isFullWidth &&
        css`
            width: 100%;
        `}

    ${({ direction }) =>
        resolveResponsiveProp({
            type: 'flex-direction',
            value: direction || 'column',
        })}

    ${spacingMixin}

    & > * {
        border-color: ${({ theme, borderColor }) =>
            theme.colors[borderColor || 'grayscale_200']} !important;

        ${({
            gap,
            borderWidth,
            direction,
            hasLastItemBorder,
            isDashed,
            turnOffChildTopBorder,
        }) => {
            const { isTablet } = useResolutions()
            const isDirectionArray = Array.isArray(direction)
            const mobileDirection = isDirectionArray
                ? direction[0]
                : direction || 'column'

            const desktopDirection = isDirectionArray
                ? direction[1]
                : direction || 'column'

            const borderFromDirection = {
                column: 'border-top',
                'column-reverse': 'border-top',
                row: 'border-left',
                'row-reverse': 'border-left',
            }

            const paddingFromDirection = {
                column: 'padding-top',
                'column-reverse': 'padding-top',
                row: 'padding-left',
                'row-reverse': 'padding-left',
            }

            const marginFromDirection = {
                column: 'margin-top',
                'column-reverse': 'margin-top',
                row: 'margin-left',
                'row-reverse': 'margin-left',
            }

            const borderType = isDashed ? 'dashed' : 'solid'

            return css`
                & + * {
                    ${resolveResponsiveProp({
                        type: isTablet
                            ? borderFromDirection[mobileDirection]
                            : borderFromDirection[desktopDirection],
                        value: borderWidth || 1,
                        unit: `px ${borderType}`,
                    })}
                    ${resolveResponsiveProp({
                        type: isTablet
                            ? paddingFromDirection[mobileDirection]
                            : paddingFromDirection[desktopDirection],
                        value: gap,
                        unit: 'px',
                    })}
                    ${resolveResponsiveProp({
                        type: isTablet
                            ? marginFromDirection[mobileDirection]
                            : marginFromDirection[desktopDirection],
                        value: gap,
                        unit: 'px',
                    })}
                }

                &:is(:nth-child(${turnOffChildTopBorder?.join(
                                '), :nth-child('
                            )})) {
                    border-top: 0px !important;
                }

                &:last-child {
                    ${hasLastItemBorder &&
                    direction === 'column' &&
                    css`
                        border-bottom: ${borderWidth}px ${borderType};

                        ${resolveResponsiveProp({
                            type: 'padding-bottom',
                            value: gap,
                            unit: 'px',
                        })}
                    `}
                }
            `
        }}
    }
`

export default SeparatedList
